import React, { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Importing icons for password visibility toggle
import config from "../../../config";
import { encodeQueryParameter } from "../../../CommonFunctions";
import { useNavigate } from "react-router-dom";
interface SignupFormProps {
  setIsLogin: (value: boolean) => void;
  signUpReroute?: string;
}

const SignupForm: React.FC<SignupFormProps> = ({
  setIsLogin,
  signUpReroute,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // For toggling password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // For toggling confirm password visibility
  const [loading, setLoading] = useState(false);
  const [formMessage, setFormMessage] = useState({
    message: "",
    isError: false,
  });
  const navigate = useNavigate();
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  const validatePasswordStrength = (password: string) => {
    const criteria = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[\W_]/.test(password),
    };
    setPasswordCriteria(criteria);
    return Object.values(criteria).every(Boolean);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormMessage({ message: "", isError: false });

    if (password !== confirmPassword) {
      setFormMessage({ message: "Passwords do not match!", isError: true });
      return;
    }

    if (!validatePasswordStrength(password)) {
      setFormMessage({
        message: "Password does not meet the required criteria.",
        isError: true,
      });
      return;
    }

    setLoading(true);

    try {
      const encodedPassword = encodeQueryParameter({ password });
      const response = await fetch(`${config.API_BASE_URL}register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email: email.toLocaleLowerCase(),
          password: encodedPassword,
        }),
      });

      if (response.ok) {
        setFormMessage({
          message: "Sign-up successful! You can now log in.",
          isError: false,
        });
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setIsLogin(true);

        // Delay navigation by 1 second (1000ms)
        setTimeout(() => {
          if (signUpReroute) {
            navigate(signUpReroute);
          } else {
            navigate("/login"); // Adjust this path based on your route
          }
        }, 1000);
      } else {
        const errorData = await response.json();
        setFormMessage({
          message: errorData[0] || "Sign-up failed",
          isError: true,
        });
      }
    } catch {
      setFormMessage({
        message: "Sign-up error. Please try again.",
        isError: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignup}>
      <div className="mb-3">
        <input
          type="text"
          className="login-page-form-control"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
      </div>
      <div className="mb-3">
        <input
          type="text"
          className="login-page-form-control"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
      </div>
      <div className="mb-3">
        <input
          type="email"
          className="login-page-form-control"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="mb-3 position-relative">
        <input
          type={showPassword ? "text" : "password"} // Toggle type based on state
          className="login-page-form-control"
          placeholder="Password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            validatePasswordStrength(e.target.value);
          }}
          required
        />
        <span
          className="password-toggle-icon"
          onClick={() => setShowPassword(!showPassword)} // Toggle visibility state
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
          }}
        >
          {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
        </span>
      </div>
      <ul className="password-criteria">
        {!passwordCriteria.length && (
          <li className={passwordCriteria.length ? "valid" : "invalid"}>
            ✔ Minimum 8 characters
          </li>
        )}
        {!passwordCriteria.lowercase && (
          <li className={passwordCriteria.lowercase ? "valid" : "invalid"}>
            ✔ At least one lowercase letter
          </li>
        )}
        {!passwordCriteria.uppercase && (
          <li className={passwordCriteria.uppercase ? "valid" : "invalid"}>
            ✔ At least one uppercase letter
          </li>
        )}
        {!passwordCriteria.number && (
          <li className={passwordCriteria.number ? "valid" : "invalid"}>
            ✔ At least one number
          </li>
        )}
        {!passwordCriteria.specialChar && (
          <li className={passwordCriteria.specialChar ? "valid" : "invalid"}>
            ✔ At least one special character
          </li>
        )}
      </ul>
      <div className="mb-3 position-relative">
        <input
          type={showConfirmPassword ? "text" : "password"} // Toggle type for confirm password
          className="login-page-form-control"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        <span
          className="password-toggle-icon"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)} // Toggle visibility state
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
          }}
        >
          {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
        </span>
      </div>
      {formMessage.message && (
        <p className={formMessage.isError ? "text-danger" : "text-success"}>
          {formMessage.message}
        </p>
      )}
      <button type="submit" className="btn login-page-btn" disabled={loading}>
        {loading ? "Signing up..." : "Sign Up"}
      </button>
    </form>
  );
};

export default SignupForm;
